import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  genericObjectSort,
  SortProperty,
  SORT_DIRECTION,
} from '../../utils/object-sort';
import styles from './DataTable.css';

const SortArrow = ({ isSorted, sortDirection }) =>
  (isSorted
    ? <i className="material-icons">
        {sortDirection === SORT_DIRECTION.ASC
          ? 'arrow_drop_up'
          : 'arrow_drop_down'}
      </i>
    : null);

SortArrow.propTypes = {
  isSorted: PropTypes.bool,
  sortDirection: PropTypes.number,
};

const DataTableHeader = ({
  columnDefinitions,
  theme = {},
  renderHeader,
  data,
  sortKey,
  sortDirection,
  sortChange,
}) => {
  if (typeof renderHeader === 'function') {
    return renderHeader(data);
  }
  const handleHeaderClick = key => () => sortChange(key);
  return (
    <thead className={classnames(styles.thead, theme.thead)}>
      <tr className={classnames(styles.row, theme.row)}>
        {Object.keys(columnDefinitions).map(col => {
          const colDef = columnDefinitions[col];
          const colDefIsObject = typeof colDef === 'object';
          const isSortable =
            colDefIsObject && (colDef.sortType || 'none') !== 'none';
          const isSorted = col === sortKey;
          const colProps = {
            style: colDefIsObject ? colDef.titleStyle : {},
            className: classnames(
              styles.cellHeading,
              colDefIsObject ? colDef.titleClassName : '',
              theme.cellHeading,
              {
                [styles.sortable]: isSortable,
              }
            ),
            key: col,
            onClick: isSortable ? handleHeaderClick(col) : null,
          };
          return (
            <th {...colProps}>
              <div>
                <span>{colDefIsObject ? colDef.title : colDef}</span>
                <SortArrow isSorted={isSorted} sortDirection={sortDirection} />
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

DataTableHeader.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columnDefinitions: PropTypes.object.isRequired,
  theme: PropTypes.shape({
    thead: PropTypes.string,
    row: PropTypes.string,
    cellHeading: PropTypes.string,
  }),
  sortChange: PropTypes.func,
  sortKey: PropTypes.string,
  sortDirection: PropTypes.number,
  renderHeader: PropTypes.func,
};

const DataTableFooter = ({ data, renderFooter }) => {
  if (typeof renderFooter === 'function') {
    return renderFooter(data);
  }
  return null;
};

DataTableFooter.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  renderFooter: PropTypes.func,
};

const DataTableBody = ({
  data = [],
  columnDefinitions = {},
  keyProp = 'key',
  theme = {},
  children,
  sortKey,
  sortDirection,
}) => {
  if (data.length === 0) {
    return React.Children.only(children);
  }
  let sortProp = null;
  let sortedData = data;
  if (sortKey) {
    sortProp = new SortProperty(sortKey, sortDirection === SORT_DIRECTION.ASC);
    sortedData = data.sort(genericObjectSort(sortProp));
  }
  return (
    <tbody className={classnames(styles.tbody, theme.tbody)}>
      {sortedData.map(rowData => (
        <tr
          key={rowData[keyProp]}
          className={classnames(styles.row, theme.row)}
        >
          {Object.keys(columnDefinitions).map((colName, index) => {
            const colDef = columnDefinitions[colName];
            const colValue = rowData[colName];
            let colRenderValue = colValue;
            if (typeof colValue === 'function') {
              colRenderValue = colValue(colValue, rowData);
            } else if (typeof colDef.render === 'function') {
              colRenderValue = colDef.render(colValue, rowData);
            }
            return (
              <td
                key={index}
                className={classnames(styles.cellData, theme.cellData)}
              >
                {colRenderValue}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};

DataTableBody.propTypes = {
  sortKey: PropTypes.string,
  sortDirection: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.object),
  columnDefinitions: PropTypes.object,
  keyProp: PropTypes.string,
  theme: PropTypes.shape({
    tbody: PropTypes.string,
    row: PropTypes.string,
    cellData: PropTypes.string,
  }),
  children: PropTypes.node,
};

export default class DataTable extends React.Component {
  static propTypes = {
    // data structure
    // [{ 'key': 'row-key', column1: dataForFirstCol, column2... etc. }]
    data: PropTypes.arrayOf(PropTypes.object),
    // ColumnDefinition structure
    // { [colKey]: ('columnTitle' || { title: 'columnTitle', render: fn }) }
    columnDefinitions: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          titleStyle: PropTypes.object,
          titleClassName: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
          ]),
          title: PropTypes.node,
          sortType: PropTypes.oneOf(['none', 'string', 'date', 'number']),
          render: PropTypes.func,
        }),
      ])
    ).isRequired,
    keyProp: PropTypes.string,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    theme: PropTypes.shape({
      table: PropTypes.string,
      thead: PropTypes.string,
      tbody: PropTypes.string,
      tfoot: PropTypes.string,
      row: PropTypes.string,
      cellHeading: PropTypes.string,
      cellData: PropTypes.string,
    }),
    style: PropTypes.object,
    renderFooter: PropTypes.func,
    renderHeader: PropTypes.func,
    // this is displayed with DataTableBody when no data is presented
    children: PropTypes.node,
    sortKey: PropTypes.string,
    sortDirection: PropTypes.oneOf([
      ...Object.values(SORT_DIRECTION),
      ...Object.keys(SORT_DIRECTION),
    ]),
  };

  constructor(props) {
    super(props);
    const sortDir = typeof props.sortDirection === 'string'
      ? SORT_DIRECTION[props.sortDirection]
      : props.sortDirection;
    this.state = {
      sortKey: props.sortKey || null,
      sortDirection: sortDir || SORT_DIRECTION.ASC,
    };
  }

  handleSortChange = key => {
    this.setState(prevState => ({
      sortKey: key,
      sortDirection: prevState.sortKey === key
        ? -prevState.sortDirection
        : SORT_DIRECTION.ASC,
    }));
  };

  render() {
    const { className, theme = {}, style = {} } = this.props;
    return (
      <table
        style={style}
        className={classnames(styles.table, theme.table, className)}
      >
        <DataTableHeader
          {...this.props}
          {...this.state}
          sortChange={this.handleSortChange}
        />
        <DataTableBody {...this.props} {...this.state} />
        <DataTableFooter {...this.props} />
      </table>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/DataTable/index.js